/**
 * หน้าผล — ธีม "หนังสือเรียน" (D18 · mockup docs/ui-book-theme.html)
 *
 * ★ แยก "ยังไม่ได้ตอบ" ออกจาก "ตอบผิด" เสมอ — สองอย่างนี้บอกคนละเรื่อง
 *   ตอบผิด = เข้าใจผิด · ไม่ได้ตอบ = ทำไม่ทัน อ่านไม่ทัน หรือข้ามเพราะไม่รู้เลย
 *
 * ★ ตัวเลือกในเฉลยเรียงตาม `shown_choice_ids` ของ attempt นั้น จึงเป็น
 *   **ลำดับเดียวกับที่เด็กเห็นตอนทำ** ไม่ใช่ลำดับในฐานข้อมูล
 *   ส่วนการชี้ว่าตัวไหนถูกใช้ `correct_choice_id` ซึ่งไม่ขึ้นกับลำดับเลย (10-INFRA §12.12)
 *
 * ★ "แยกตามหัวข้อ" นับจาก `topic_name` ของแต่ละข้อใน attempt นี้ · "ข้อที่พลาด" กด "ดูเฉลย"
 *   แล้วเลื่อนไปการ์ดเฉลยของข้อนั้นจริง (ไม่มี route ใหม่) · ไม่มีดาว/เทียบรอบก่อน (ยังไม่มีข้อมูล)
 */
import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { AnswerBox } from "@/components/AnswerBox";
import { Figure } from "@/components/Figure";
import { Passage } from "@/components/Passage";
import { Center, Shell } from "@/components/Shell";
import type { Stroke } from "@/features/pad/strokes";
import { translate as t } from "@/i18n";
import { api } from "@/lib/api";
import { renderMath } from "@/lib/math";
import { pickPraise } from "@/lib/praise";
import { colorClass, useBookSkin } from "@/lib/book";
import { loadStrokesLocal } from "@/lib/offline";
import type { AttemptAnswerResult, AttemptResult } from "@/lib/types";

import { StrokeReplay } from "./QuizPage";

const KEYS = ["ก", "ข", "ค", "ง", "จ", "ฉ"];

interface TopicRow {
  name: string;
  correct: number;
  total: number;
}

function byTopic(answers: AttemptAnswerResult[], fallback: string): TopicRow[] {
  const rows = new Map<string, TopicRow>();
  for (const a of answers) {
    if (a.excluded) continue;
    const name = a.topic_name ?? fallback;
    const row = rows.get(name) ?? { name, correct: 0, total: 0 };
    row.total += 1;
    if (a.is_correct) row.correct += 1;
    rows.set(name, row);
  }
  return [...rows.values()];
}

function choiceText(a: AttemptAnswerResult, id: string | null): string {
  return a.choices.find((c) => c.id === id)?.text ?? "";
}

function scrollToAnswer(questionId: string): void {
  // การ์ดเฉลยเพิ่งถูกเรนเดอร์ — รอเฟรมถัดไปให้ DOM มีก่อน
  window.requestAnimationFrame(() => {
    document
      .getElementById(`ans-${questionId}`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  });
}

export function ResultPage(): JSX.Element {
  useBookSkin();
  const { attemptId = "" } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const preloaded = (location.state as { result?: AttemptResult } | null)
    ?.result;

  const [showAnswers, setShowAnswers] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ["result", attemptId],
    initialData: preloaded,
    queryFn: () => api.get<AttemptResult>(`/attempts/${attemptId}/result`),
  });

  if (isLoading || !data) {
    return (
      <Shell progress={1}>
        <Center>
          <p>{t("state.loading")}</p>
        </Center>
      </Shell>
    );
  }

  const counted = data.total_count - data.excluded_count;
  const pct =
    counted > 0 ? Math.round((data.correct_count / counted) * 100) : 0;
  const ck = colorClass(data.subject_color_key);
  const rows = byTopic(
    data.answers,
    data.subject_name_th ?? t("book.otherTopic"),
  );
  const missed = data.answers.filter((a) => !a.excluded && !a.is_correct);
  const firstMiss = missed[0];
  // D24 — หัวข้อเป็นศัพท์วัยรุ่นสุ่มตามช่วงคะแนน · เมล็ดสุ่มคือ attempt id
  //   จึงได้คำเดิมทุกครั้งที่เปิดผลชุดเดิม (รีเฟรชแล้วคำไม่เปลี่ยน)
  const praise = pickPraise(pct, data.id);
  // บรรทัดข้อเท็จจริงใต้คำชม — ถ้อยคำเดิมของแต่ละช่วงคะแนน ไม่แตะ
  const facts =
    pct >= 60
      ? t("book.resultPass", "th", { pct })
      : t("book.scoreOnly", "th", { pct });
  const took =
    data.duration_sec !== null
      ? t("book.took", "th", {
          m: Math.floor(data.duration_sec / 60),
          s: data.duration_sec % 60,
        })
      : null;
  const context = [data.subject_name_th, data.quiz_title, took]
    .filter(Boolean)
    .join(" · ");

  function reveal(questionId: string): void {
    setShowAnswers(true);
    scrollToAnswer(questionId);
  }

  return (
    <Shell lockText progress={1} here={t("book.resultHere")}>
      <div className={`bk-result ${ck}`} data-testid="result">
        <div className="hero">
          <div
            className="bk-ring"
            style={{ ["--pct" as string]: pct }}
            data-testid="ring"
          >
            <div>
              <b>
                {data.correct_count}
                <span>/{counted}</span>
              </b>
              <small>{t("book.questionsUnit")}</small>
            </div>
          </div>
          <div>
            {/* ★ lang="en" เพื่อให้ตัวเรนเดอร์เลือกฟอนต์อังกฤษ ไม่ใช่ฟอนต์ไทยที่
                ตัวพิมพ์ใหญ่ล้วนจะดูแน่นและอ่านยากบน iPad */}
            <h1 className="slang" lang="en" data-testid="praise">
              {praise.slang}
            </h1>
            <p className="read" data-testid="praise-read">
              ({praise.read}) · {praise.meaning}
            </p>
            <p className="facts" data-testid="facts">
              {data.correct_count}/{counted} · {facts}
            </p>
            {context ? <p>{context}</p> : null}
          </div>
        </div>

        <div className="bk-bd">
          <h3>{t("book.byTopic")}</h3>
          <div data-testid="by-topic">
            {rows.map((r) => (
              <div key={r.name} className="bk-row">
                <div className="nm">
                  <span className="dot" />
                  {r.name}
                </div>
                <div className="rt">
                  <span className="bk-bar" aria-hidden="true">
                    <i
                      style={{
                        width: `${r.total ? Math.round((r.correct / r.total) * 100) : 0}%`,
                      }}
                    />
                  </span>
                  <span className="num">
                    {r.correct}/{r.total}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <h3>
            {missed.length
              ? t("book.missed", "th", { n: missed.length })
              : t("book.noMissed")}
          </h3>
          {missed.length ? (
            <ul className="bk-miss" data-testid="missed">
              {missed.map((a) => (
                <li key={a.question_id}>
                  <span className="q">
                    {t("book.qNo", "th", { n: a.order_index + 1 })}
                  </span>
                  <span>
                    {renderMath(a.stem)}
                    <br />
                    <span className="g">
                      {a.topic_name ?? ""}
                      {a.topic_name ? " · " : ""}
                      {renderMath(
                        a.answered
                          ? t("book.answered", "th", {
                              chosen: choiceText(a, a.chosen_choice_id),
                              correct: choiceText(a, a.correct_choice_id),
                            })
                          : t("book.unanswered", "th", {
                              correct: choiceText(a, a.correct_choice_id),
                            }),
                      )}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="re"
                    data-reveal={a.question_id}
                    onClick={() => reveal(a.question_id)}
                  >
                    {t("book.seeAnswer")}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}

          {data.unanswered_count > 0 ? (
            <p className="hint">
              {t("result.skipped")}: {data.unanswered_count} ·{" "}
              {t("result.note")}
            </p>
          ) : (
            <p className="hint">{t("result.note")}</p>
          )}

          <div className="bk-acts">
            {firstMiss ? (
              <button
                type="button"
                className="bk-pill"
                onClick={() => reveal(firstMiss.question_id)}
              >
                {t("book.reviewMissed")}
              </button>
            ) : null}
            {!showAnswers ? (
              <button
                type="button"
                className="bk-pill ghost"
                onClick={() => setShowAnswers(true)}
              >
                {t("result.review")}
              </button>
            ) : null}
            {/* ★ ทางออกท้ายหน้า — หน้านี้ทำเสร็จแล้ว ไม่มีอะไรจะเสีย ออกได้ทันทีโดยไม่ต้องถาม */}
            <button
              type="button"
              className="bk-pill ghost"
              data-exit="home"
              onClick={() =>
                navigate(
                  data.subject_code ? `/learn/${data.subject_code}` : "/learn",
                )
              }
            >
              {t("book.backToTopics")}
            </button>
          </div>
        </div>
      </div>

      {showAnswers ? (
        <div style={{ marginTop: 28 }}>
          {data.answers.map((a) => (
            <AnswerCard
              key={a.question_id}
              answer={a}
              attemptId={attemptId}
              colorKey={data.subject_color_key}
            />
          ))}
        </div>
      ) : null}
    </Shell>
  );
}

function AnswerCard({
  answer,
  attemptId,
  colorKey,
}: {
  answer: AttemptAnswerResult;
  attemptId: string;
  colorKey: string | null | undefined;
}): JSX.Element {
  const [strokes, setStrokes] = useState<Stroke[]>([]);

  useEffect(() => {
    void loadStrokesLocal(attemptId, answer.question_id).then(setStrokes);
  }, [attemptId, answer.question_id]);

  if (answer.excluded) {
    return (
      <div
        className="fb"
        id={`ans-${answer.question_id}`}
        style={{ marginTop: 18 }}
      >
        <div className="fb-body">
          <p className="qtext" style={{ fontSize: 19 }}>
            {renderMath(answer.stem)}
          </p>
          <div className="note" style={{ marginTop: 0 }}>
            {t("quiz.excluded")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="review"
      id={`ans-${answer.question_id}`}
      style={{ marginTop: 18 }}
    >
      <div className="qcard">
        <div className="qcard-top">
          {t("book.qNo", "th", { n: answer.order_index + 1 })}
        </div>
        <div className="qcard-body">
          {answer.passage ? <Passage text={answer.passage} /> : null}
          {/* D23 — รูปเดียวกับตอนทำข้อสอบ ไม่งั้นอ่านเฉลยแล้วไม่รู้ว่าโจทย์พูดถึงรูปไหน */}
          <Figure svg={answer.figure} />
          <p className="qtext" style={{ fontSize: 19, margin: "0 0 16px" }}>
            {renderMath(answer.stem)}
          </p>
          <div className="choices">
            {/* ★ เทียบด้วย `choice.id` — ตำแหน่งใช้แค่เลือกตัวอักษร ก/ข/ค/ง ที่โชว์
                หน้านี้กับหน้าทำข้อสอบเรียงคนละลำดับได้ แต่ต้องชี้ตัวเดียวกันเสมอ */}
            {answer.choices.map((choice, position) => {
              let cls = "ch";
              if (choice.id === answer.correct_choice_id) cls = "ch right";
              else if (choice.id === answer.chosen_choice_id)
                cls = "ch chosen-wrong";
              return (
                <div
                  key={choice.id}
                  className={cls}
                  style={{ cursor: "default" }}
                >
                  <span className="k">{KEYS[position]}</span>
                  <span>{renderMath(choice.text)}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* D20 — กล่องเฉลยแบบใหม่ (components/AnswerBox) · ข้อความตัวเลือกที่ถูกหาจาก id */}
      <AnswerBox
        correct={answer.is_correct}
        correctText={
          answer.choices.find((c) => c.id === answer.correct_choice_id)?.text ??
          null
        }
        explanation={answer.explanation}
        tip={answer.tip}
        keyTerms={answer.key_terms}
        sourceDisplay={answer.source_display}
        sourceBook={answer.source_book}
        colorKey={colorKey}
      >
        {strokes.length > 0 ? (
          <div className="work">
            <h3>{t("quiz.myWork")}</h3>
            <StrokeReplay strokes={strokes} />
          </div>
        ) : null}
      </AnswerBox>
    </div>
  );
}
